<template>
  <v-row no-gutters align="start">
    <v-col cols="12">
      <span class="caption font-weight-medium">
        {{ translatedLabel }}
      </span>
    </v-col>
    <v-col cols="6">
      <dropdown
        v-model="period"
        :items="items"
        @change="changePeriod"
        :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
        class="calendar-middle-field"
        :hide-details="hideDetails"
      >
      </dropdown>
    </v-col>
    <v-col cols="6">
      <v-menu
        ref="menu"
        class="rounded-lg custom-shadow"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="model"
        transition="scale-transition"
        offset-y
        min-width="auto"
        nudge-left="150px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="date(model)"
            prepend-inner-icon="mdi-chevron-left"
            append-icon="mdi-chevron-right"
            @click:prepend-inner="goToPreviousMonth"
            @click:append="goToNextMonth"
            readonly
            hide-details
            :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
            class="input-text-center custom-field calendar-field"
            solo
            flat
            filled
            dense
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          color="primary"
          class="rounded-lg custom-shadow"
          v-model="model"
          type="month"
          :locale="locale"
        >
          <v-spacer></v-spacer>
          <v-btn text @click="menu = false">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            depressed
            color="success"
            @click="$refs.menu.save(model)"
            class="custom-shadow rounded-lg"
          >
            {{ $t("save") }}
          </v-btn>
        </v-date-picker>
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasModel],
  props: {
    label: {
      type: String,
      required: false,
      default: "",
    },
    hideLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      period: "current",
      menu: false,
    };
  },
  methods: {
    changePeriod() {
      if (this.period === "custom") this.menu = true;
    },
    goToPreviousMonth() {
      const currentDate = new Date(this.model);
      const previousMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        2
      );
      this.model = previousMonth.toISOString().substr(0, 7);
    },
    date(date) {
      if (!date) return "";
      const formattedDate = this.localeDate(date + "-01", {
        month: "long",
        year: "numeric",
      });
      return formattedDate.charAt(0).toUpperCase() + formattedDate.substring(1);
    },
    goToNextMonth() {
      const currentDate = new Date(this.model);
      const previousMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        2
      );
      this.model = previousMonth.toISOString().substr(0, 7);
    },
  },
  computed: {
    allDatesInMonth() {
      if (!this.model) return [];
      const [year, month] = this.model.split("-").map(Number);
      // JavaScript months are 0-indexed, so subtract 1 from the month
      const daysInMonth = new Date(year, month, 0).getDate();
      const dates = [];
      for (let day = 1; day <= daysInMonth; day++) {
        // Format day and month to be two digits
        const formattedMonth = month < 10 ? `0${month}` : month;
        const formattedDay = day < 10 ? `0${day}` : day;
        dates.push(`${year}-${formattedMonth}-${formattedDay}`);
      }
      return dates;
    },
    locale() {
      let locale = "";
      switch (this.$i18n.locale) {
        case "en":
          locale = "en-GB";
          break;
        case "hr":
          locale = "hr-HR";
          break;
        case "sr":
          locale = "sr-RS";
          break;
      }
      return locale;
    },
    translatedLabel() {
      if (this.hideLabel) return "";
      if (!this.label) return this.$t("date");
      else return this.label;
    },
    items() {
      return [
        {
          text: this.$t("times.currentMonth"),
          value: "current",
        },
        {
          text: this.$t("times.previousMonth"),
          value: "previous",
        },
        {
          text: this.$t("times.nextMonth"),
          value: "next",
        },
        {
          text: this.$t("times.custom"),
          value: "custom",
        },
      ];
    },
    items2() {
      return [
        {
          text: this.$t("yearly"),
          value: "current",
        },
        {
          text: this.$t("monthly"),
          value: "previous",
        },
        {
          text: this.$t("times.custom"),
          value: "custom",
        },
      ];
    },
  },
  watch: {
    allDatesInMonth: {
      handler(newValue) {
        this.$emit("updatedMonthDates", newValue);
      },
      immediate: true,
    },
    period(period) {
      if (period === "current") {
        const currentDate = new Date();
        this.model = currentDate.toISOString().substr(0, 7);
      } else if (period === "next") {
        const currentDate = new Date();
        const nextMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          2
        );
        this.model = nextMonth.toISOString().substr(0, 7);
      } else if (period === "previous") {
        const currentDate = new Date();
        const previousMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - 1,
          2
        );
        this.model = previousMonth.toISOString().substr(0, 7);
      }
    },

    model(date) {
      const today = new Date();
      const selected = new Date(date);

      // Clear the time component for both dates
      today.setHours(0, 0, 0, 0);
      selected.setHours(0, 0, 0, 0);

      // Compare the dates
      if (
        selected.getFullYear() === today.getFullYear() &&
        selected.getMonth() === today.getMonth()
      ) {
        this.period = "current";
      } else if (
        selected.getFullYear() === today.getFullYear() &&
        selected.getMonth() === today.getMonth() - 1
      ) {
        this.period = "previous";
      } else if (
        selected.getFullYear() === today.getFullYear() &&
        selected.getMonth() === today.getMonth() + 1
      ) {
        this.period = "next";
      } else {
        this.period = "custom";
      }
    },
  },
};
</script>

<style>
.input-hide-border fieldset {
  border-right: none !important;
}

.input-text-center input {
  text-align: center;
}

.calendar-middle-field .v-input__slot {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.calendar-field .v-input__slot {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>
